<template>
    <TransitionRoot as="template" :show="opened">
        <Dialog class="relative z-50" @close="opened = false">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black bg-opacity-75 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 w-screen">
                <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="relative w-full transform rounded-md bg-white p-4 text-left shadow-xl transition-all sm:my-8"
                            :class="{
                                'sm:w-full sm:max-w-xl md:max-w-3xl lg:max-w-5xl': size === 'extra_large',
                                'sm:w-full sm:max-w-sm md:max-w-xl lg:max-w-2xl': size === 'large',
                                'max-w-xs': size === 'small'
                            }">
                            <div
                                class="w-8 h-8 border bg-sn-light-grey border-sn-midlight-grey rounded-full cursor-pointer top-4 right-4 absolute flex justify-center items-center"
                                @click="opened = false"
                            >
                                <i
                                    class="text-base fal fa-times"
                                />
                            </div>

                            <div>
                                <slot name="title">
                                    <div
                                        v-if="props.title"
                                        class="pb-2 mb-2 pr-10 pt-0.5"
                                    >
                                        <h3
                                            class="text-xl font-medium"
                                            v-text="props.title"
                                        />
                                    </div>
                                </slot>
                                <div class="max-h-[calc(100vh-136px)]"
                                     :class="{'overflow-y-scroll': overflowActive}"
                                     style="scrollbar-color: #C7C7C7 transparent;">
                                    <div ref="container">
                                        <slot />
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import {useElementSize} from "@vueuse/core";
import {useTemplateRef} from "vue";

const opened = ref(false);
const container = useTemplateRef('container');
const { height } = useElementSize(container);

const props = defineProps({
    title: {
        type: String,
        required: false,
    },
    size: {
        type: String as PropType<'small' | 'large' | 'extra_large'>,
        default: 'large',
    }
})

const overflowActive = computed(() => {
    return height.value > window.innerHeight - 136;
});

function open(event?: Event) {
    event?.preventDefault();
    opened.value = true;
}

function close(event?: Event) {
    event?.preventDefault();
    opened.value = false;
}

defineExpose({ open, close });
</script>